import React, { useRef, useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import { Dropdown, Menu } from "antd";
import {
  Container,
  Typography,
  CardContent,
  Grid,
  Box,
  Paper,
  Button,
  Stack,
  Divider
} from "@mui/material";
import ReactToPrint from "react-to-print";

// Import your report components
import Passenger, { exportExcelPassengers } from "../reports/Passenger";
import Driver, { exportExcelDrivers } from "../reports/Driver";
import Trips, { exportExcelTrips } from "../reports/Trips";
import BookedTrips, { exportExcelBookedTrips } from "../reports/BookedTrips";
import Transactions, { exportExcelTransactions } from "../reports/Transactions";
import CorporateTransactions, {
  exportExcelCorporateTransactions
} from "../reports/CorporateTransactions";
import CompanyLists, {
  exportExcelCorporateAccounts
} from "../reports/CompanyLists";

const REPORT_TYPES = [
  {
    id: "passengers",
    label: "Passenger Report",
    description: "Download detailed passenger information",
    exportExcel: exportExcelPassengers,
    component: Passenger
  },
  {
    id: "drivers",
    label: "Driver Report",
    description: "Access driver statistics",
    exportExcel: exportExcelDrivers,
    component: Driver
  },
  {
    id: "trips",
    label: "Trip Report",
    description: "View trip history",
    exportExcel: exportExcelTrips,
    component: Trips
  },
  {
    id: "bookedTrips",
    label: "Booked Trip Report",
    description: "Pre-booked trip details",
    exportExcel: exportExcelBookedTrips,
    component: BookedTrips
  },
  {
    id: "transactions",
    label: "Transaction Report",
    description: "Financial transaction records",
    exportExcel: exportExcelTransactions,
    component: Transactions
  },
  {
    id: "corporateTransactions",
    label: "Corporate Transaction Report",
    description: "Business transaction details",
    exportExcel: exportExcelCorporateTransactions,
    component: CorporateTransactions
  },
  {
    id: "corporateAccounts",
    label: "Corporate Account Report",
    description: "Corporate account overview",
    exportExcel: exportExcelCorporateAccounts,
    component: CompanyLists
  }
];

// Example styling
const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    backgroundColor: "#f5f7fb",
  },
  headerWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    mb: 3,
  },
  title: {
    color: "#0f172a",
    fontWeight: 700,
    letterSpacing: "-0.02em",
  },
  subTitle: {
    color: "#475569",
    fontWeight: 400,
    maxWidth: 760,
  },
  card: {
    height: "100%",
    borderRadius: 2,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 12px 24px rgba(15, 23, 42, 0.10)",
    },
  },
  content: {
    p: 2.5,
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
  },
  cardTitle: {
    fontWeight: 700,
    color: "#0f172a",
  },
  cardDesc: {
    color: "#64748b",
    minHeight: 40,
  },
  actionsRow: {
    mt: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    flexWrap: "wrap",
  },
  primaryButton: {
    textTransform: "none",
    borderRadius: 1.5,
    fontWeight: 600,
  },
  secondaryButton: {
    textTransform: "none",
    borderRadius: 1.5,
    fontWeight: 600,
  },
  dropdownButton: {
    minWidth: 40,
    padding: "8px",
    borderRadius: 1.5,
  },
  previewWrap: {
    mt: 2,
    borderRadius: 2,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    backgroundColor: "#ffffff",
    overflow: "auto",
    maxHeight: 420,
  },
  previewInner: {
    p: 2,
  },
};

export default function Report() {
  const [visibleReports, setVisibleReports] = useState({});
  const refs = REPORT_TYPES.reduce(
    (acc, type) => ({ ...acc, [type.id]: useRef() }),
    {}
  );

  const handleVisibilityToggle = (reportId) => {
    setVisibleReports((prev) => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  const exportMenu = (report) => (
    <Menu>
      <Menu.Item key="excel" onClick={report.exportExcel}>
        <Typography>Export as Excel</Typography>
      </Menu.Item>
    </Menu>
  );

  return (
    <Box sx={styles.page}>
      <Container
        maxWidth="xl"
        sx={{
          pt: 4,
          pb: 4,
          px: { xs: 2, md: 5 }
        }}
      >
        <Box sx={styles.headerWrap}>
          <Typography variant="h4" align="left" sx={styles.title}>
            Reports Dashboard
          </Typography>
          <Typography variant="body1" sx={styles.subTitle}>
            Choose a report, preview it, and export as PDF or Excel.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {REPORT_TYPES.map((report) => (
            <Grid item xs={12} sm={6} md={4} key={report.id}>
              <Paper elevation={0} sx={styles.card}>
                <CardContent sx={styles.content}>
                  <Typography variant="subtitle1" align="left" sx={styles.cardTitle}>
                    {report.label}
                  </Typography>
                  <Typography variant="body2" align="left" sx={styles.cardDesc}>
                    {report.description}
                  </Typography>

                  <Divider />

                  <Box sx={styles.actionsRow}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <Button
                        variant={visibleReports[report.id] ? "outlined" : "contained"}
                        color="primary"
                        sx={styles.primaryButton}
                        onClick={() => handleVisibilityToggle(report.id)}
                      >
                        {visibleReports[report.id] ? "Hide Preview" : "Preview"}
                      </Button>

                      <ReactToPrint
                        trigger={() => (
                          <Button
                            variant="contained"
                            color="primary"
                            sx={styles.primaryButton}
                            disabled={!visibleReports[report.id]}
                          >
                            Export PDF
                          </Button>
                        )}
                        content={() => refs[report.id].current}
                      />
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        sx={styles.secondaryButton}
                        onClick={report.exportExcel}
                      >
                        Export Excel
                      </Button>

                      <Dropdown overlay={exportMenu(report)} trigger={["click"]}>
                        <Button variant="outlined" sx={styles.dropdownButton}>
                          <DownOutlined />
                        </Button>
                      </Dropdown>
                    </Stack>
                  </Box>

                  {visibleReports[report.id] ? (
                    <Box sx={styles.previewWrap}>
                      <Box sx={styles.previewInner}>
                        <report.component prRef={refs[report.id]} />
                      </Box>
                    </Box>
                  ) : null}
                </CardContent>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
